import React, { useEffect, useMemo, useState } from "react";
import {
  useAddContactsToListMutation,
  useGetContactListContactsQuery,
  useGetContactsQuery,
} from "@/store/rest_api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

interface Props {
  open: boolean;
  onClose: () => void;
  listId: string;
}

export default function AddContactsModal({ open, onClose, listId }: Props) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);

  // Contacts search (pick from all contacts)
  const { data: contactsPage, isFetching: contactsFetching } = useGetContactsQuery({
    page,
    size: 20,
    q,
    sort: "createdAt,desc",
  });

  // Existing list members (so we can exclude them)
  // Note: size can be increased; for very large lists you’ll eventually want a BE “excludeIds” or “notInList” query.
  const { data: listMembersPage, isFetching: membersFetching } = useGetContactListContactsQuery({
    listId,
    page: 0,
    size: 5000,
  });

  const [addContacts, { isLoading: isSaving }] = useAddContactsToListMutation();

  // Reset to first page when searching
  useEffect(() => {
    setPage(0);
  }, [q]);

  // Build a Set of contactIds already in the list
  const existingIds = useMemo(() => {
    const items = listMembersPage?.items ?? [];
    const ids = new Set<string>();

    for (const item of items) {
      const id = (item as any)?.contact?.id;
      if (id) ids.add(id);
    }
    return ids;
  }, [listMembersPage]);

  const allContacts = contactsPage?.items ?? [];

  const availableContacts = useMemo(() => {
    // Exclude contacts already in list
    return allContacts.filter((c) => !existingIds.has(c.id));
  }, [allContacts, existingIds]);

  const alreadyInListCount = allContacts.length - availableContacts.length;

  const toggle = (id: string) => {
    if (existingIds.has(id)) return; // safety guard
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const removeSelected = (id: string) => {
    setSelected((prev) => prev.filter((x) => x !== id));
  };

  const handleSubmit = async () => {
    try {
      await addContacts({ listId, contactIds: selected }).unwrap();
      toast.success("Contacts added");
      setSelected([]);
      onClose();
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to add contacts");
    }
  };

  const isBusy = contactsFetching || membersFetching;

  return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <div className="p-6 pb-4">
            <DialogHeader>
              <DialogTitle className="text-xl">Add contacts to list</DialogTitle>
            </DialogHeader>

            {/* Search */}
            <div className="mt-4 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Search by name, company..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-9"
                />
              </div>

              <Badge variant="secondary" className="whitespace-nowrap">
                Page {page + 1}
              </Badge>
            </div>

            {/* Selected chips */}
            {selected.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="default">{selected.length} selected</Badge>
                  <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setSelected([])}
                  >
                    Clear
                  </Button>
                </div>
            )}

            {alreadyInListCount > 0 && (
                <div className="mt-3 text-xs text-muted-foreground">
                  {alreadyInListCount} result{alreadyInListCount === 1 ? "" : "s"} hidden (already in this list)
                </div>
            )}
          </div>

          {/* List */}
          <div className="border-t">
            <div className="max-h-[360px] overflow-y-auto">
              {isBusy && availableContacts.length === 0 ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
              ) : availableContacts.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    No contacts found.
                  </div>
              ) : (
                  <ul className="divide-y">
                    {availableContacts.map((c) => {
                      const checked = selected.includes(c.id);
                      const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || "—";
                      return (
                          <li
                              key={c.id}
                              className="flex items-center gap-3 px-6 py-3 hover:bg-muted/40 cursor-pointer"
                              onClick={() => toggle(c.id)}
                          >
                            <div
                                className={`h-5 w-5 rounded border flex items-center justify-center ${
                                    checked ? "bg-primary text-primary-foreground border-primary" : "bg-background"
                                }`}
                            >
                              {checked && <Check className="h-4 w-4" />}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">{name}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {c.company ? c.company : "—"}
                                {c.position ? ` • ${c.position}` : ""}
                              </div>
                            </div>

                            {c.type && (
                                <Badge variant="outline" className="shrink-0">
                                  {c.type}
                                </Badge>
                            )}
                          </li>
                      );
                    })}
                  </ul>
              )}
            </div>

            {/* Pagination row */}
            <div className="flex items-center justify-between px-6 py-3 border-t bg-background">
              <div className="text-xs text-muted-foreground">
                Showing {availableContacts.length} of {contactsPage?.size ?? 20}
              </div>

              <div className="flex gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 0 || contactsFetching}
                    onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>

                <Button
                    size="sm"
                    variant="outline"
                    disabled={contactsPage?.last || contactsFetching}
                    onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>

            {/* Sticky actions */}
            <div className="flex items-center justify-between px-6 py-4 border-t bg-background">
              <div className="text-sm text-muted-foreground">
                {selected.length > 0 ? (
                    <>
                      Ready to add <span className="font-medium text-foreground">{selected.length}</span> contact
                      {selected.length === 1 ? "" : "s"}.
                    </>
                ) : (
                    "Select contacts to add."
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={selected.length === 0 || isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add ({selected.length})
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
  );
}